import React, { createContext, useContext, useState, useEffect } from 'react';
import { logoutUser, clearCachedProfile } from '../api/insights';
import { identifyUser } from '../analytics/mixpanel';

const AuthContext = createContext();
const SCOPED_STORAGE_PREFIXES = ['insightsDashboardState', 'insightsConnectionFiltersState'];

const clearScopedSessionState = () => {
    try {
        for (const key of SCOPED_STORAGE_PREFIXES) {
            sessionStorage.removeItem(key); // legacy unscoped key
        }
        const toRemove = [];
        for (let i = 0; i < sessionStorage.length; i += 1) {
            const k = sessionStorage.key(i);
            if (!k) continue;
            if (SCOPED_STORAGE_PREFIXES.some((p) => k.startsWith(`${p}:`))) {
                toRemove.push(k);
            }
        }
        toRemove.forEach((k) => sessionStorage.removeItem(k));
    } catch (err) {
        console.error('Failed to clear scoped session state', err);
    }
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [authModalCallback, setAuthModalCallback] = useState(null);

    const logout = async () => {
        try {
            await logoutUser();
        } catch (err) {
            console.error('Failed to logout on server', err);
        }
        setUser(null);
        clearCachedProfile();
        clearScopedSessionState();
        localStorage.removeItem('insights_user');
        localStorage.removeItem('insights_token');
        sessionStorage.removeItem('insights_token');
    };

    useEffect(() => {
        // Load user from localStorage on init
        const savedUser = localStorage.getItem('insights_user');
        const token = localStorage.getItem('insights_token');
        if (savedUser && token) {
            try {
                const parsedUser = JSON.parse(savedUser);
                setUser(parsedUser);
                identifyUser(parsedUser, { created_at: new Date().toISOString() });
            } catch (e) {
                console.error('Failed to parse user', e);
            }
        } else if (savedUser || token) {
            // Inconsistent state: clear both
            localStorage.removeItem('insights_user');
            localStorage.removeItem('insights_token');
        }

        const handleAuthError = () => {
            logout();
        };

        window.addEventListener('auth-error', handleAuthError);
        setLoading(false);

        return () => {
            window.removeEventListener('auth-error', handleAuthError);
        };
    }, []);

    const login = (userData, token) => {
        clearScopedSessionState();
        setUser(userData);
        localStorage.setItem('insights_user', JSON.stringify(userData));
        if (token) {
            localStorage.setItem('insights_token', token);
            sessionStorage.removeItem('insights_token'); // Cleanup old loc
        }
        identifyUser(userData, { created_at: new Date().toISOString() });
    };

    const openLogin = (callback = null) => {
        setAuthModalCallback(() => callback);
        setIsAuthModalOpen(true);
    };

    const closeLogin = () => {
        setIsAuthModalOpen(false);
        setAuthModalCallback(null);
    };

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            login,
            logout,
            isAuthModalOpen,
            openLogin,
            closeLogin,
            authModalCallback
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
